import React from "react";
import { X, Plus } from "lucide-react";
import { Character, Attack } from "./types";
import { StatCard } from "./StatCard";

interface CombatTabProps {
	character: Character;
	updateCharacter: (path: string, value: any) => void;
	addItem: (category: keyof Character, item: any) => void;
	removeItem: (category: keyof Character, index: number) => void;
}

export const CombatTab: React.FC<CombatTabProps> = ({
	character,
	updateCharacter,
	addItem,
	removeItem,
}) => (
	<div className="space-y-8">
		{/* Combat Stats */}
		<StatCard title="Combat Statistics">
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				<div className="text-center">
					<label className="block text-xs font-semibold mb-3 text-gray-600 uppercase tracking-wide">
						Armor Class
					</label>
					<div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
						<input
							type="number"
							value={character.armorClass}
							onChange={(e) =>
								updateCharacter(
									"armorClass",
									parseInt(e.target.value) || 10,
								)
							}
							className="w-full text-center text-3xl font-bold bg-transparent border-none outline-none text-red-600"
						/>
					</div>
				</div>
				<div className="text-center">
					<label className="block text-xs font-semibold mb-3 text-gray-600 uppercase tracking-wide">
						Initiative
					</label>
					<div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
						<input
							type="number"
							value={character.initiative}
							onChange={(e) =>
								updateCharacter(
									"initiative",
									parseInt(e.target.value) || 0,
								)
							}
							className="w-full text-center text-3xl font-bold bg-transparent border-none outline-none text-amber-600"
						/>
					</div>
				</div>
				<div className="text-center">
					<label className="block text-xs font-semibold mb-3 text-gray-600 uppercase tracking-wide">
						Speed
					</label>
					<div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
						<input
							type="number"
							value={character.speed}
							onChange={(e) =>
								updateCharacter(
									"speed",
									parseInt(e.target.value) || 30,
								)
							}
							className="w-full text-center text-3xl font-bold bg-transparent border-none outline-none text-blue-600"
						/>
					</div>
				</div>
			</div>
		</StatCard>

		{/* Hit Points */}
		<StatCard title="Hit Points">
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				<div className="text-center">
					<label className="block text-xs font-semibold mb-3 text-gray-600 uppercase tracking-wide">
						Current HP
					</label>
					<div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
						<input
							type="number"
							value={character.hitPointsCurrent}
							onChange={(e) =>
								updateCharacter(
									"hitPointsCurrent",
									parseInt(e.target.value) || 0,
								)
							}
							className="w-full text-center text-3xl font-bold bg-transparent border-none outline-none text-green-600"
						/>
					</div>
				</div>
				<div className="text-center">
					<label className="block text-xs font-semibold mb-3 text-gray-600 uppercase tracking-wide">
						Max HP
					</label>
					<div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
						<input
							type="number"
							value={character.hitPointsMax}
							onChange={(e) =>
								updateCharacter(
									"hitPointsMax",
									parseInt(e.target.value) || 10,
								)
							}
							className="w-full text-center text-3xl font-bold bg-transparent border-none outline-none text-green-600"
						/>
					</div>
				</div>
				<div className="text-center">
					<label className="block text-xs font-semibold mb-3 text-gray-600 uppercase tracking-wide">
						Temporary HP
					</label>
					<div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
						<input
							type="number"
							value={character.tempHitPoints}
							onChange={(e) =>
								updateCharacter(
									"tempHitPoints",
									parseInt(e.target.value) || 0,
								)
							}
							className="w-full text-center text-3xl font-bold bg-transparent border-none outline-none text-purple-600"
						/>
					</div>
				</div>
			</div>
		</StatCard>

		{/* Death Saves */}
		<StatCard title="Death Saves">
			<div className="grid grid-cols-2 gap-8">
				<div>
					<label className="block text-sm font-semibold mb-4 text-green-700">
						Successes
					</label>
					<div className="flex gap-3 justify-center">
						{[1, 2, 3].map((i) => (
							<button
								key={i}
								onClick={() =>
									updateCharacter(
										"deathSaves.successes",
										character.deathSaves.successes >= i
											? i - 1
											: i,
									)
								}
								className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
									character.deathSaves.successes >= i
										? "bg-green-500 border-green-500"
										: "border-green-300 hover:border-green-500"
								}`}
							/>
						))}
					</div>
				</div>
				<div>
					<label className="block text-sm font-semibold mb-4 text-red-700">
						Failures
					</label>
					<div className="flex gap-3 justify-center">
						{[1, 2, 3].map((i) => (
							<button
								key={i}
								onClick={() =>
									updateCharacter(
										"deathSaves.failures",
										character.deathSaves.failures >= i
											? i - 1
											: i,
									)
								}
								className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
									character.deathSaves.failures >= i
										? "bg-red-500 border-red-500"
										: "border-red-300 hover:border-red-500"
								}`}
							/>
						))}
					</div>
				</div>
			</div>
		</StatCard>

		{/* Attacks */}
		<StatCard title="Attacks & Spellcasting">
			<div className="space-y-4">
				{character.attacks.map((attack, index) => (
					<div
						key={index}
						className="flex gap-3 items-center p-4 bg-gray-50 rounded-xl"
					>
						<input
							type="text"
							value={attack.name || ""}
							onChange={(e) => {
								const newAttacks = [...character.attacks];
								newAttacks[index] = {
									...newAttacks[index],
									name: e.target.value,
								};
								updateCharacter("attacks", newAttacks);
							}}
							placeholder="Weapon/Spell"
							className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200"
						/>
						<input
							type="text"
							value={attack.bonus || ""}
							onChange={(e) => {
								const newAttacks = [...character.attacks];
								newAttacks[index] = {
									...newAttacks[index],
									bonus: e.target.value,
								};
								updateCharacter("attacks", newAttacks);
							}}
							placeholder="+0"
							className="w-20 px-3 py-2 border-2 border-gray-200 rounded-lg text-center focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200"
						/>
						<input
							type="text"
							value={attack.damage || ""}
							onChange={(e) => {
								const newAttacks = [...character.attacks];
								newAttacks[index] = {
									...newAttacks[index],
									damage: e.target.value,
								};
								updateCharacter("attacks", newAttacks);
							}}
							placeholder="1d8+0"
							className="w-50 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200"
						/>
						<button
							onClick={() => removeItem("attacks", index)}
							className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
						>
							<X size={16} />
						</button>
					</div>
				))}
				<button
					onClick={() =>
						addItem("attacks", {
							name: "",
							bonus: "",
							damage: "",
						})
					}
					className="flex items-center gap-2 px-6 py-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors duration-200 font-semibold"
				>
					<Plus size={16} />
					Add Attack
				</button>
			</div>
		</StatCard>
	</div>
);
